<script setup lang="ts">
// ================================
// IMPORTS
// ================================
// Vue
import { computed, ref } from "vue"
import { useI18n } from "vue-i18n"

// Helpers
import { formatDate } from "@core/helpers"
import moment from "moment"

// Constant
import { CONSENT_REQUEST_STATUS } from "@core/constants"

// Type
import { NotificationEventTask } from "@types"
import { ToastMessageOptions, useConfirm, useToast } from "primevue"
import { useEventBus } from "@vueuse/core"
import { useId } from "vue"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            create_date: ({ named }: { named: (key: string) => string }) => {
                if (named("futur"))
                    return `${named("date")} \n Relance automatique prévue  ${named("mail") ? `\n pour ${named("mail")}` : ""}`
                else if (named("send"))
                    return `${named("date")} \n Demande ${
                        named("again") ? "renvoyée" : "envoyée"
                    } ${named("mail") ? `\n à ${named("mail")}` : ""}`
                else
                    return `${named("date")}\n Demande créée ${named("mail") ? `\n pour ${named("mail")}` : ""}`
            },
            sign_date: ({ named }: { named: (key: string) => string }) =>
                `Consentement signé le ${named("date")}`,
            refused_date: "Demande refusée",
            component: {
                meter_card_invitation_share: {
                    title: "Demande de partage",
                    timeline: {
                        step1: {
                            label: "Formulation de la demande",
                        },
                        step2: {
                            label: "En attente de validation du partage",
                        },
                    },
                },
            },
            send_again: ({ named }: { named: (key: string) => string }) =>
                `Relancer ${named("multi") ? "la demande groupée" : "ce titulaire"}`,
            send_one: "Envoyer",
            delete: {
                button: "Supprimer",
                confirm: "Êtes-vous sûr de vouloir supprimer la demande ?",
                info: "Cette action est irréversible.",
                success: "La demande a bien été supprimée.",
                error: "Une erreur est survenue lors de la suppression.",
                cancel: "Annuler",
                accept: "Supprimer",
                reject: "Annuler",
            },
        },
    },
})

// ================================
// TYPES & INTERFACES
// ================================

type TrackingMail = {
    type: string
    date: string
}

type Props = {
    // Données de la demande
    state: string
    type: string
    createdAt: string
    url?: string
    requestId: string
    isMultiRequest?: boolean
    // Tracking
    trackingMails?: TrackingMail[]
    // Informations pour l'affichage
    userToEmail?: string
    sourceId?: string
    /** Libellé affiché dans {@link ConsentRequestDeleteDialog} (ex. idsource), pas l’`_id_source` Mongo. */
    sourceLabel?: string
    // Indique si le bouton de suppression doit être désactivé
    disableDelete?: boolean
    // Callbacks
    onSendInvitation: (config: { sendBack: boolean }) => void
    /** Si fourni : confirmation PrimeVue + cet appel. Sinon : {@link ConsentRequestDeleteDialog}. */
    onDelete?: () => Promise<{ isFetching: () => boolean }>
}

type StatusType = "current" | "completed" | "error" | "warning" | "disabled"

type Step = {
    label: string
    status: string
    helper?: string
}

// ================================
// PROPS & EMITS
// ================================
const props = defineProps<Props>()

// ================================
// CONSTANTS
// ================================
const statusColorMap: Record<string, string> = {
    current: "border-orange-500 bg-orange-500 text-white",
    completed: "border-green-500 bg-green-500 text-white",
    error: "border-red-500 bg-red-500",
    warning: "border-red-500 bg-red-500",
    disabled: "border-blue-700",
}

const statusIconMap: Record<string, string> = {
    completed: "pi pi-check",
    current: "pi pi-spinner",
    error: "pi pi-times",
    warning: "pi pi-exclamation-circle",
}

const componentId = useId()

const deleteDialogVisible = ref(false)

// ================================
// COMPUTED PROPERTIES
// ================================

/**
 * Permet de récupérer la date du dernier mail de relance
 */
const getLastMailDate = computed(() => {
    if (!props.trackingMails?.length) return null

    const relaunchMails = props.trackingMails.filter(
        (m: TrackingMail) => m.type === "relance"
    )

    // si aucun mail de relance alors on renvoie la date du premier mail
    if (relaunchMails.length === 0) {
        return formatDate(props.trackingMails[0]?.date, "DD/MM/YYYY HH:MM")
    }

    const lastDate = getFirstDateOfRelaunch(props.trackingMails)
    return formatDate(lastDate, "DD/MM/YYYY HH:MM")
})

/**
 * Computed pour récupérer les étapes de la demande de partage
 */
const getSteps = computed((): Step[] => {
    const getStepHelper = (type = "invitation") => {
        if (!props.createdAt) return
        let date
        let sendAgain
        let mail: TrackingMail | undefined

        // Récupère le mail du type demandé
        if (props.trackingMails) {
            const matchingMails = props.trackingMails.filter(
                (m: TrackingMail) => m.type === type
            )
            mail =
                matchingMails.length > 0
                    ? matchingMails[matchingMails.length - 1]
                    : undefined
        }

        // date de création de la demande ou d'envoie du mail d'invitation
        date = mail
            ? mail.date
            : formatDate(props.createdAt, "DD/MM/YYYY HH:MM")

        if (mail && type === "invitation") {
            date = formatDate(mail.date, "DD/MM/YYYY HH:MM")
            sendAgain = true
        } else if (mail) {
            date = getLastMailDate.value
            sendAgain = true
        }

        return t("create_date", {
            // date de création de la demande ou d'envoie du mail d'invitation
            date,
            // indique si la demande a été envoyé par mail
            send: sendAgain,
            again: type === "relance",
            // si on à le mail du signataire on l'affiche
            mail: props.userToEmail,
            // si le mail est envoyé dans le futur: relance automatique
            futur: moment(date, "DD/MM/YYYY HH:mm").isAfter(moment()),
        })
    }

    return [
        {
            label: t(
                "component.meter_card_invitation_share.timeline.step1.label"
            ),
            helper: getStepHelper(),
            status: "completed" as StatusType,
        },
        {
            label: t(
                "component.meter_card_invitation_share.timeline.step2.label"
            ),
            helper: [CONSENT_REQUEST_STATUS.REFUSED].includes(props.state)
                ? t("refused_date")
                : getStepHelper("relance"),
            status: [
                CONSENT_REQUEST_STATUS.REFUSED,
                CONSENT_REQUEST_STATUS.EXPIRED,
            ].includes(props.state)
                ? "error"
                : "current",
        },
    ]
})

/**
 * Permet de récupérer le titre du bouton pour envoyer ou relancer
 */
const getButtonTitle = computed(() => {
    const hasMails = (props.trackingMails?.length ?? 0) > 0

    if (props.state === CONSENT_REQUEST_STATUS.CREATED && hasMails) {
        return {
            label: t("send_again", {
                multi: !!props.isMultiRequest,
            }),
            action: () => {
                props.onSendInvitation({
                    sendBack: true,
                })
            },
        }
    } else if (props.state === CONSENT_REQUEST_STATUS.CREATED && !hasMails) {
        return {
            label: t("send_one"),
            action: () => {
                props.onSendInvitation({
                    sendBack: false,
                })
            },
        }
    } else return null
})

// ================================
// METHODS
// ================================

/**
 * Permet de récupérer la date du premier mail de relance dans une liste de mails envoyées.
 * La stratégie est de récupérer tous les mails de type "relance".
 * Si existe alors on récupère le mail de relance N-2
 * Sinon on récupère le mail de relance N-1
 * ou le mail de relance N
 * @param mails - liste des mails
 */
const getFirstDateOfRelaunch = (
    mails: TrackingMail[]
): string | null | undefined => {
    // si aucun mail de relance
    if (!mails?.length) return null
    // récupère seulement les 3 derniers mails de relance
    const relaunchMails = mails.filter((m) => m.type === "relance").slice(-3)
    if (relaunchMails?.length === 0) return undefined
    // mail N
    else if (relaunchMails.length === 1) return relaunchMails[0]?.date
    // mail N-2
    else if (relaunchMails.length === 2)
        return relaunchMails[relaunchMails.length - 2]?.date
    // mail N-3
    else return relaunchMails[relaunchMails.length - 3]?.date
}

// ================================
// Suppression de la demande
// ================================

const confirm = useConfirm()
const busTask = useEventBus<NotificationEventTask>("task")
const toast = useToast()

function onClickDelete(): void {
    if (props.onDelete) {
        confirmDeleteConsentRequest()
        return
    }
    if (props.sourceId) {
        deleteDialogVisible.value = true
    }
}

function confirmDeleteConsentRequest(): void {
    if (!props.onDelete) return
    confirm.require({
        group: componentId,
        message: t("delete.info"),
        header: t("delete.confirm"),
        icon: "pi pi-info-circle",
        rejectLabel: t("delete.cancel"),
        rejectProps: {
            label: t("delete.reject"),
            outlined: true,
            severity: "secondary",
        },
        acceptProps: {
            label: t("delete.accept"),
            icon: "pi pi-trash",
            severity: "danger",
        },
        accept: async () => {
            let notification: ToastMessageOptions | null = null

            function callback(_notification: ToastMessageOptions) {
                notification = _notification
            }
            busTask.emit({
                severity: "info",
                title: "Suppression en cours",
                message:
                    "La demande est en cours de suppression, veuillez patienter...",
                callback,
            })

            const deletePromise = props.onDelete!()
            if (deletePromise) {
                await deletePromise
                if (notification) toast.remove(notification)
            } else {
                if (notification) toast.remove(notification)
            }
        },
    })
}
</script>
<template>
    <div
        class="meter_card_invitation_share flex flex-col items-center gap-6 w-full"
    >
        <!-- Timeline pour afficher le statis d ela demande -->
        <BaseTimeline :value="getSteps" class="w-full">
            <template #marker="slotProps">
                <!-- icone -->
                <span
                    :class="[
                        'w-6 h-6 flex items-center justify-center rounded-full border',
                        statusColorMap[slotProps.item.status],
                    ]"
                >
                    <i
                        v-if="statusIconMap[slotProps.item.status]"
                        :class="[
                            'text-white block',
                            statusIconMap[slotProps.item.status],
                        ]"
                    ></i>
                </span>
            </template>
            <!-- texte à gauche: nom de l'étape -->
            <template #opposite="slotProps">
                {{ slotProps.item.label }}
            </template>
            <!-- texte à droite: infos supplémentaires: destinataire, date,... -->
            <template #content="slotProps">
                <div class="flex flex-col">
                    <small class="text-surface-500 whitespace-pre-line">{{
                        slotProps.item.helper
                    }}</small>
                </div>
            </template>
        </BaseTimeline>
        <!-- Boutons pour envoyer ou renvoyer la demande et supprimer -->
        <div class="flex flex-row gap-2">
            <BaseButton
                icon="pi pi-envelope"
                v-if="getButtonTitle"
                severity="contrast"
                variant="outlined"
                :label="getButtonTitle.label"
                @click="getButtonTitle.action?.()"
            />
            <BaseButton
                v-if="!disableDelete && (onDelete || sourceId)"
                severity="danger"
                icon="pi pi-trash"
                outlined
                :label="t('delete.button')"
                @click="onClickDelete()"
            />
        </div>
        <BaseConfirmDialog
            v-if="onDelete"
            :group="componentId"
        ></BaseConfirmDialog>
        <ConsentRequestDeleteDialog
            v-if="!onDelete && sourceId"
            v-model:visible="deleteDialogVisible"
            :source-id="sourceId"
            :source-label="sourceLabel"
        />
    </div>
</template>
<style scoped></style>
